import React, { useState, useRef } from "react"
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  Tooltip,
  LinearProgress,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import ImageIcon from "@mui/icons-material/Image"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import DescriptionIcon from "@mui/icons-material/Description"
import { useTranslation } from "react-i18next"

import { uploadFiles, deleteFileByInfo } from "../../helpers/fileStorage"

const getFileIcon = (fileName) => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) {
    return <ImageIcon color="primary" />
  }
  if (ext === "pdf") {
    return <PictureAsPdfIcon color="error" />
  }
  if (["doc", "docx", "txt", "rtf"].includes(ext)) {
    return <DescriptionIcon color="info" />
  }
  return <InsertDriveFileIcon color="action" />
}

const formatFileSize = (bytes) => {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const StepFileUpload = ({ files = [], onFilesChange, stepName }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files)
    if (selectedFiles.length === 0) return

    setUploading(true)
    setUploadProgress(0)

    try {
      // Sanitize folder name
      const folder = (stepName || "general").replace(/[^a-zA-Z0-9-_]/g, "_")

      const uploadedFiles = await uploadFiles(selectedFiles, folder, (progress) => {
        setUploadProgress(progress)
      })

      if (uploadedFiles.length > 0) {
        onFilesChange([...files, ...uploadedFiles])
      }
    } catch (error) {
      console.error("Error uploading files:", error)
    } finally {
      setUploading(false)
      setUploadProgress(0)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const handleDelete = async (index) => {
    const fileToDelete = files[index]

    // Try to delete from server (optional - remove from list even if server delete fails)
    if (fileToDelete.folder && fileToDelete.filename) {
      await deleteFileByInfo(fileToDelete)
    }

    // Remove from list
    const newFiles = files.filter((_, i) => i !== index)
    onFilesChange(newFiles)
  }

  const handleDownload = (file) => {
    window.open(file.url, "_blank")
  }

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        style={{ display: "none" }}
        accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
      />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <AttachFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ borderRadius: 2 }}
        >
          {uploading ? t("Uploading...") : t("Add Files")}
        </Button>
        {files.length > 0 && (
          <Chip
            label={`${files.length} ${t("files")}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>

      {uploading && (
        <Box sx={{ width: "100%", mb: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            {uploadProgress}% {t("uploaded")}
          </Typography>
        </Box>
      )}

      {files.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {files.map((file, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              {getFileIcon(file.name)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                {file.size && (
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                )}
              </Box>
              <Tooltip title={t("Download")}>
                <IconButton size="small" onClick={() => handleDownload(file)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Delete")}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  )
}

export default StepFileUpload
